/**
 * VillaEntry — scroll + click video state machine.
 *
 * States: idle -> intro -> keypad -> (presaleLive)
 *   live: doorOpen -> interiorEntry -> interiorIdle (loop, final)
 *   off:  doorJammed -> windowVent -> interiorDim (still, final)
 */
class VillaEntry {
  constructor(root, options) {
    if (!root) return;
    this.root = root;
    this.presaleLive = options.presaleLive;
    this.isMobile = options.isMobile;
    this.assets = options.assets;

    this.videoA = root.querySelector('.villa-entry__video--a');
    this.videoB = root.querySelector('.villa-entry__video--b');
    this.activeVideo = this.videoA;
    this.idleVideo = this.videoB;
    this.stillEl = root.querySelector('.villa-entry__still');

    this.overlays = {
      idle: root.querySelector('[data-state="idle"]'),
      keypad: root.querySelector('[data-state="keypad"]'),
      finalLive: root.querySelector('[data-state="final-live"]'),
      finalOff: root.querySelector('[data-state="final-off"]')
    };

    this.hasStarted = false;
    this.state = 'idle';

    this.bindEvents();
    this.enterIdle();
  }

  srcFor(assetKey) {
    const asset = this.assets[assetKey];
    if (!asset) return '';
    if (this.isMobile && asset.mobile) return asset.mobile;
    return asset.desktop || '';
  }

  showOverlay(name) {
    Object.entries(this.overlays).forEach(([key, el]) => {
      if (!el) return;
      const visible = key === name;
      el.hidden = !visible;
      el.classList.toggle('villa-entry__overlay--visible', visible);
    });
  }

  playVideo(assetKey, { loop = false, onEnded = null } = {}) {
    const src = this.srcFor(assetKey);
    this.stillEl.classList.remove('villa-entry__still--active');

    const incoming = this.activeVideo === this.videoA ? this.videoB : this.videoA;
    const outgoing = this.activeVideo;

    if (!src) {
      // Missing asset — fail gracefully, still advance the flow.
      if (onEnded) onEnded();
      return;
    }

    incoming.src = src;
    incoming.loop = loop;
    incoming.currentTime = 0;

    const handleEnded = () => {
      incoming.removeEventListener('ended', handleEnded);
      if (onEnded) onEnded();
    };
    if (!loop && onEnded) incoming.addEventListener('ended', handleEnded);

    incoming.play().catch(() => {});
    incoming.classList.add('villa-entry__video--active');
    outgoing.classList.remove('villa-entry__video--active');
    outgoing.pause();

    this.activeVideo = incoming;
  }

  showStill(assetKey) {
    const src = this.srcFor(assetKey);
    if (!src) return;
    this.activeVideo.classList.remove('villa-entry__video--active');
    this.activeVideo.pause();
    this.stillEl.src = src;
    this.stillEl.classList.add('villa-entry__still--active');
  }

  enterIdle() {
    this.state = 'idle';
    this.showOverlay('idle');
    this.playVideo('idle', { loop: true });
  }

  beginApproach() {
    if (this.hasStarted) return;
    this.hasStarted = true;
    this.state = 'intro';
    this.showOverlay(null);
    this.playVideo('intro', {
      loop: false,
      onEnded: () => this.enterKeypad()
    });
  }

  enterKeypad() {
    this.state = 'keypad';
    this.showStill('keypadStill');
    this.showOverlay('keypad');
  }

  enterBranch() {
    if (this.state !== 'keypad') return;
    this.showOverlay(null);
    if (this.presaleLive) {
      this.state = 'door-open';
      this.playVideo('doorOpen', {
        loop: false,
        onEnded: () => {
          this.state = 'interior-entry';
          this.playVideo('interiorEntry', {
            loop: false,
            onEnded: () => this.enterFinalLive()
          });
        }
      });
    } else {
      this.state = 'door-jammed';
      this.playVideo('doorJammed', {
        loop: false,
        onEnded: () => {
          this.state = 'window-vent';
          this.playVideo('windowVent', {
            loop: false,
            onEnded: () => this.enterFinalOff()
          });
        }
      });
    }
  }

  enterFinalLive() {
    this.state = 'final-live';
    this.playVideo('interiorIdle', { loop: true });
    this.showOverlay('finalLive');
  }

  enterFinalOff() {
    this.state = 'final-off';
    this.showStill('interiorDim');
    this.showOverlay('finalOff');
  }

  bindEvents() {
    const scrollCta = this.root.querySelector('.villa-entry__scroll-cta');
    if (scrollCta) {
      scrollCta.addEventListener('click', () => this.beginApproach());
    }

    // First scroll intent (wheel/touch) also begins the approach, once.
    const startOnScroll = () => {
      this.beginApproach();
      window.removeEventListener('wheel', startOnScroll);
      window.removeEventListener('touchmove', startOnScroll);
    };
    window.addEventListener('wheel', startOnScroll, { passive: true, once: true });
    window.addEventListener('touchmove', startOnScroll, { passive: true, once: true });

    const keypad = this.root.querySelector('.villa-entry__keypad');
    if (keypad) {
      keypad.addEventListener('click', () => this.enterBranch());
    }
  }
}